/**
 * ADO #273 — Project-wide reference scanner for outline Delete actions.
 *
 * Pure helper. Walks the project's flows + sessions + files looking for
 * anything that references a target (flow / file / type) and returns a
 * normalised list the Delete dialog can render and tour.
 *
 * No store reads — caller passes the project state in. Mirrors the
 * pattern used by codeNextProgress / codeNextScope.
 *
 * Each reference is shaped:
 *   {
 *     id:        string,             // unique within the result set
 *     flowId:    string,             // flow that contains the referencing node
 *     flowName:  string,
 *     fileName:  string | null,
 *     nodeId:    string | null,      // for node-level refs (Subflow / Action)
 *     kind:      string,             // 'subflow-call' | 'action-call' |
 *                                    // 'variable-type' | 'parameter-type' |
 *                                    // 'return-type' | 'field-type' |
 *                                    // 'extends' | 'implements' | 'include'
 *     label:     string,             // short human description used in the
 *                                    // dialog row ("Subflow node 'add' calling subtract")
 *   }
 */

// ── Helpers ────────────────────────────────────────────────────────────────

// Walks every meaningful node inside a flow tree (decision branches +
// loop bodies + try/catch branches). Returns a flat list. Mirrors the
// walker used in buildGuidedSteps / codeNextScope.
function flattenFlowNodes(nodes) {
  const out = [];
  const walk = (list) => {
    for (const n of list || []) {
      if (!n) continue;
      out.push(n);
      if (n.type === 'decision' && Array.isArray(n.outcomes)) {
        for (const o of n.outcomes) walk(o.nodes);
      } else if (n.type === 'loop') {
        walk(n.loopBody);
        walk(n.afterLast);
      } else if (n.type === 'try_catch' && Array.isArray(n.branches)) {
        for (const b of n.branches) walk(b.nodes);
      }
    }
  };
  walk(nodes);
  return out;
}

const fileNameForFlow = (flow, files) => {
  if (flow.fileName) return flow.fileName;
  const file = (files || []).find(f => f.id === flow.fileId);
  return file?.name || null;
};

const refId = (() => {
  let n = 0;
  return () => `ref-${++n}`;
})();

// Strip generic / pointer / array sugar so a Java field declared as
// `List<Box>` or a C++ field of `Box*` still matches the type 'Box'.
function baseTypeName(typeStr) {
  if (!typeStr) return '';
  return String(typeStr)
    .replace(/<.*?>/g, '')        // strip generics
    .replace(/\[\]/g, '')         // strip Java []
    .replace(/[*&]/g, '')         // strip C/C++ pointer/reference
    .replace(/^const\s+/, '')     // strip const qualifier
    .trim();
}

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Find every reference to a specific flow (function / method / constructor).
 *
 * Reference kinds:
 *   - subflow-call   — Subflow node whose targetFlowId matches
 *   - action-call    — Action node naming the flow as its functionName
 *
 * @param {string} targetFlowId  ID of the flow the student wants to delete.
 * @param {object} project       { flows, files, globalResources, ... }
 * @returns {Array<Reference>}
 */
export function findReferencesToFlow(targetFlowId, project) {
  if (!targetFlowId || !project) return [];
  const flows = project.flows || [];
  const files = project.files || [];
  const target = flows.find(f => f.id === targetFlowId);
  if (!target) return [];
  const targetName = target.name;

  const out = [];
  for (const flow of flows) {
    if (flow.id === targetFlowId) continue; // skip the flow being deleted
    const fileName = fileNameForFlow(flow, files);
    const nodes = flattenFlowNodes(flow.nodes || []);
    for (const node of nodes) {
      if (node.type === 'subflow' && node.targetFlowId === targetFlowId) {
        out.push({
          id: refId(),
          flowId: flow.id,
          flowName: flow.name,
          fileName,
          nodeId: node.id,
          kind: 'subflow-call',
          label: `Subflow call to ${targetName}() in ${flow.name}()`,
        });
      } else if (node.type === 'action') {
        const name = node.functionName || node.actionFunction || node.label || '';
        const cleaned = String(name).replace(/\(.*\)\s*$/, '').trim();
        if (cleaned && cleaned === targetName) {
          out.push({
            id: refId(),
            flowId: flow.id,
            flowName: flow.name,
            fileName,
            nodeId: node.id,
            kind: 'action-call',
            label: `Action call to ${targetName}() in ${flow.name}()`,
          });
        }
      }
    }
  }
  return out;
}

/**
 * Find every reference to a TYPE name — Java class / record / interface /
 * enum, C struct, C++ class. Walks every flow's resources, parameters,
 * returnType, and (for Java) extends / implements declarations on
 * sessions; plus any resource whose dataType matches.
 *
 * @param {string} typeName  bare type identifier (e.g. 'Box', 'Node')
 * @param {object} project   { flows, files, sessions, globalResources, ... }
 * @returns {Array<Reference>}
 */
export function findReferencesToType(typeName, project) {
  if (!typeName || !project) return [];
  const flows = project.flows || [];
  const files = project.files || [];
  const sessions = project.sessions || [];

  const out = [];
  const matches = (t) => baseTypeName(t) === typeName;

  for (const flow of flows) {
    const fileName = fileNameForFlow(flow, files);
    // Parameters
    for (const p of flow.parameters || []) {
      if (matches(p.type || p.dataType)) {
        out.push({
          id: refId(),
          flowId: flow.id,
          flowName: flow.name,
          fileName,
          nodeId: null,
          kind: 'parameter-type',
          label: `Parameter ${p.name} : ${typeName} in ${flow.name}()`,
        });
      }
    }
    // Return type
    if (matches(flow.returnType)) {
      out.push({
        id: refId(),
        flowId: flow.id,
        flowName: flow.name,
        fileName,
        nodeId: null,
        kind: 'return-type',
        label: `Return type ${typeName} of ${flow.name}()`,
      });
    }
    // Local resources / variables in this flow
    for (const r of flow.resources || []) {
      if (r._fromParameter) continue; // already counted via parameters above
      if (matches(r.dataType || r.type)) {
        out.push({
          id: refId(),
          flowId: flow.id,
          flowName: flow.name,
          fileName,
          nodeId: null,
          kind: 'variable-type',
          label: `Variable ${r.name} : ${typeName} in ${flow.name}()`,
        });
      }
    }
  }

  // Java extends / implements live on session metadata.
  for (const s of sessions) {
    const sessionData = s.sessionData || {};
    const ext = sessionData.extends || sessionData.extendsName;
    if (ext && baseTypeName(ext) === typeName) {
      out.push({
        id: refId(),
        flowId: null,
        flowName: s.name,
        fileName: (files.find(f => f.id === s.fileId) || {}).name || null,
        nodeId: null,
        kind: 'extends',
        label: `${s.name} extends ${typeName}`,
      });
    }
    const impls = sessionData.implements || sessionData.implementsList || [];
    for (const i of (Array.isArray(impls) ? impls : [impls]).filter(Boolean)) {
      if (baseTypeName(i) === typeName) {
        out.push({
          id: refId(),
          flowId: null,
          flowName: s.name,
          fileName: (files.find(f => f.id === s.fileId) || {}).name || null,
          nodeId: null,
          kind: 'implements',
          label: `${s.name} implements ${typeName}`,
        });
      }
    }
  }

  // Project-wide globals (struct fields / class fields).
  for (const r of project.globalResources || []) {
    if (matches(r.dataType || r.type)) {
      out.push({
        id: refId(),
        flowId: null,
        flowName: null,
        fileName: null,
        nodeId: null,
        kind: 'field-type',
        label: `Global ${r.name} : ${typeName}`,
      });
    }
  }

  return out;
}

/**
 * Find every reference to a FILE — aggregates references to every flow
 * the file owns + #include directives in other files (C/C++).
 *
 * @param {string} fileId
 * @param {object} project   { flows, files, sessions, globalResources, ... }
 * @returns {Array<Reference>}
 */
export function findReferencesToFile(fileId, project) {
  if (!fileId || !project) return [];
  const flows = project.flows || [];
  const files = project.files || [];
  const file = files.find(f => f.id === fileId);
  if (!file) return [];

  // Flows that LIVE in this file are the ones we're deleting; references
  // come from flows OUTSIDE the file.
  const inFile = new Set(
    flows
      .filter(f => f.fileName === file.name || f.fileId === fileId)
      .map(f => f.id),
  );

  const out = [];
  for (const flowId of inFile) {
    out.push(...findReferencesToFlow(flowId, project)
      .filter(ref => !inFile.has(ref.flowId))); // skip self-references inside the file
  }

  // C / C++ #include detection — any flow in another file whose nodes
  // mention #include "<this-file-base>.h" in code/text.
  // Skipped for v1 — auto-#includes are emitted by the codegen, not authored
  // by the student, so deleting a header is rare. Add later if needed.

  // Deduplicate by flowId + nodeId combo.
  const seen = new Set();
  return out.filter((r) => {
    const key = `${r.flowId}:${r.nodeId}:${r.kind}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Force-delete companion to the scanner — walks a flow's node tree
 * (decision branches + loop bodies + try/catch) and stamps a
 * `_brokenReference` marker on any Subflow / Action node that points
 * at the deleted flow. Returns a NEW flow object when changes were
 * made, or null when nothing matched.
 *
 * Tagging shape:
 *   node._brokenReference = { kind, name, originalId, reason }
 * where:
 *   kind        — 'subflow' | 'action'
 *   name        — the deleted flow's name
 *   originalId  — the deleted flow's id (only meaningful for Subflow)
 *   reason      — short human-readable string for the canvas badge
 *
 * The scanner and the tagger MUST stay in sync — every reference the
 * scanner reports has to be reachable by the tagger so a Force-delete
 * never leaves an undetected dangling pointer.
 *
 * @param {object} flow                 The flow whose nodes to walk.
 * @param {{id, name}} deletedFlow      Identity of the flow being removed.
 * @returns {object|null}               Updated flow, or null if no change.
 */
export function tagBrokenFlowRefs(flow, deletedFlow) {
  if (!flow || !deletedFlow) return null;
  const { id: deletedId, name: deletedName } = deletedFlow;
  let changed = false;

  const walk = (list) => {
    if (!Array.isArray(list)) return list;
    return list.map((n) => {
      if (!n) return n;
      let next = n;

      if (n.type === 'subflow' && n.targetFlowId === deletedId) {
        next = {
          ...n,
          _brokenReference: {
            kind: 'subflow',
            name: deletedName,
            originalId: deletedId,
            reason: `Function ${deletedName}() was deleted`,
          },
        };
        changed = true;
      } else if (n.type === 'action') {
        const raw = n.functionName || n.actionFunction || n.label || '';
        const cleaned = String(raw).replace(/\(.*\)\s*$/, '').trim();
        if (cleaned && cleaned === deletedName) {
          next = {
            ...n,
            _brokenReference: {
              kind: 'action',
              name: deletedName,
              originalId: deletedId,
              reason: `Function ${deletedName}() was deleted`,
            },
          };
          changed = true;
        }
      }

      if (n.type === 'decision' && Array.isArray(n.outcomes)) {
        const newOutcomes = n.outcomes.map((o) => ({ ...o, nodes: walk(o.nodes) }));
        next = { ...next, outcomes: newOutcomes };
      } else if (n.type === 'loop') {
        next = {
          ...next,
          loopBody: walk(n.loopBody),
          afterLast: walk(n.afterLast),
        };
      } else if (n.type === 'try_catch' && Array.isArray(n.branches)) {
        next = {
          ...next,
          branches: n.branches.map((b) => ({ ...b, nodes: walk(b.nodes) })),
        };
      }
      return next;
    });
  };

  const newNodes = walk(flow.nodes || []);
  return changed ? { ...flow, nodes: newNodes } : null;
}

/**
 * Inverse of tagBrokenFlowRefs — when the student restores a flow whose
 * name matches a previously-tagged broken reference, clear the marker
 * AND repoint the Subflow node's targetFlowId to the new flow's id so
 * the call wires up automatically.
 *
 * Match is by NAME (the only stable handle the student edits) — the
 * `originalId` stored in the marker is dead by definition. Action nodes
 * just need the marker cleared since they reference the function by
 * name, not id.
 *
 * @param {object} flow                  Flow to scan.
 * @param {{id, name}} restoredFlow      Identity of the freshly-added flow.
 * @returns {object|null}                Updated flow, or null if no change.
 */
export function healBrokenFlowRefs(flow, restoredFlow) {
  if (!flow || !restoredFlow) return null;
  const { id: restoredId, name: restoredName } = restoredFlow;
  if (!restoredName) return null;
  let changed = false;

  const walk = (list) => {
    if (!Array.isArray(list)) return list;
    return list.map((n) => {
      if (!n) return n;
      let next = n;

      if (n._brokenReference && n._brokenReference.name === restoredName) {
        // Strip the marker; rebuild a clean node.
        // eslint-disable-next-line no-unused-vars
        const { _brokenReference, ...rest } = n;
        next = rest;
        if (n.type === 'subflow') {
          next = { ...next, targetFlowId: restoredId };
        }
        changed = true;
      }

      if (n.type === 'decision' && Array.isArray(n.outcomes)) {
        next = { ...next, outcomes: n.outcomes.map((o) => ({ ...o, nodes: walk(o.nodes) })) };
      } else if (n.type === 'loop') {
        next = { ...next, loopBody: walk(n.loopBody), afterLast: walk(n.afterLast) };
      } else if (n.type === 'try_catch' && Array.isArray(n.branches)) {
        next = {
          ...next,
          branches: n.branches.map((b) => ({ ...b, nodes: walk(b.nodes) })),
        };
      }
      return next;
    });
  };

  const newNodes = walk(flow.nodes || []);
  return changed ? { ...flow, nodes: newNodes } : null;
}

/**
 * Count broken-reference markers across every flow's nodes.
 * Used by the test-runner gate (ADO #273): when > 0, the runner refuses
 * to start so the student fixes their dangling references first.
 *
 * @param {Array} flows  project.flows
 * @returns {number}
 */
export function countBrokenReferences(flows) {
  if (!Array.isArray(flows)) return 0;
  let n = 0;
  for (const flow of flows) {
    for (const node of flattenFlowNodes(flow.nodes || [])) {
      if (node && node._brokenReference) n += 1;
    }
  }
  return n;
}

/**
 * Find the first broken-reference marker in any flow. Returns enough
 * context for the caller to navigate to it (open the right flow tab,
 * highlight the right node).
 *
 * @param {Array} flows  project.flows
 * @returns {{ flowId, flowName, fileName, nodeId, marker } | null}
 */
export function findFirstBrokenReference(flows) {
  if (!Array.isArray(flows)) return null;
  for (const flow of flows) {
    for (const node of flattenFlowNodes(flow.nodes || [])) {
      if (node && node._brokenReference) {
        return {
          flowId: flow.id,
          flowName: flow.name,
          fileName: flow.fileName || null,
          nodeId: node.id,
          marker: node._brokenReference,
        };
      }
    }
  }
  return null;
}

// Internal exports for testing.
export const _internal = {
  flattenFlowNodes,
  baseTypeName,
};

export default {
  findReferencesToFlow,
  findReferencesToType,
  findReferencesToFile,
};
